import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { openSignIn } from "@/lib/signInModal";
import { openPreRegister } from "@/lib/preRegisterModal";

const COLS = [
  {
    title: "Platform",
    links: [
      { label: "Drivers", href: "#drivers" },
      { label: "Find a Driver", href: "/drivers" },
      { label: "Owner Operators", href: "#owner-operators" },
      { label: "Carriers", href: "#carriers" },
      { label: "Brokers", href: "/brokers" },
      { label: "RV Rescue & Delivery", href: "#rv" },
      { label: "How It Works", href: "#how" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Safety & Trust", href: "#trust" },
      { label: "About", href: "/company" },
      { label: "Help Center", href: "#faq" },
      { label: "Contact", href: "/company#contact" },
      { label: "Report Fraud", href: "/company#report" },
    ],
  },
];

const LEGAL = [
  { label: "Terms of Service", slug: "terms-of-service" },
  { label: "Privacy Policy", slug: "privacy-policy" },
  { label: "Driver / Contractor Agreement", slug: "driver-agreement" },
  { label: "Carrier/Requester Agreement", slug: "carrier-requester-agreement" },
  { label: "Refund & Cancellation Policy", slug: "payment-terms" },
  { label: "Insurance Coverage Disclosure", slug: "insurance-disclosures" },
  { label: "Cookie Policy", slug: "cookie-policy" },
  { label: "Acceptable Use Policy", slug: "acceptable-use-policy" },
  { label: "Data Retention Schedule", slug: "data-retention" },
  { label: "Accessibility", slug: "accessibility" },
  { label: "Compliance Information", slug: "compliance-information" },
  { label: "Data Processing Agreement", slug: "data-processing-agreement" },
  { label: "Master Service Agreement", slug: "master-service-agreement" },
  { label: "FMCSA Compliance Overview", slug: "fmcsa-compliance-overview" },
];

export const Footer = () => {
  const user = useAuth();

  return (
    <footer data-testid="site-footer" className="bg-rr-navyDeep text-white pt-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr_1fr_1fr] gap-12 pb-14 border-b border-white/10">
          <div>
            <img src="/assets/relayrigs-logo.png" alt="RelayRigs" className="h-12 w-auto mb-4" />
            <p className="text-white/50 text-sm max-w-xs">Keep It Moving.</p>
            <div className="flex gap-3 mt-6">
              {user ? (
                <Link to="/account" data-testid="footer-account-link" className="text-sm font-bold border border-white/20 rounded-full px-5 py-2.5 hover:bg-white/10 transition-colors">
                  My Account
                </Link>
              ) : (
                <button onClick={() => openSignIn("signin")} data-testid="footer-signin-button" className="text-sm font-bold border border-white/20 rounded-full px-5 py-2.5 hover:bg-white/10 transition-colors">
                  Sign In
                </button>
              )}
              <button onClick={() => openPreRegister("driver")} data-testid="footer-join-cta" className="text-sm font-bold bg-rr-orange rounded-full px-5 py-2.5 hover:bg-white hover:text-rr-navy transition-colors">
                Pre-Register
              </button>
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-white/40 mb-4">{col.title}</div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-white/70 hover:text-rr-orangeLight transition-colors">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-white/40 mb-4">Legal</div>
            <ul className="space-y-3">
              {LEGAL.slice(0, 5).map((l) => (
                <li key={l.slug}>
                  <Link to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="text-sm text-white/70 hover:text-rr-orangeLight transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div data-testid="footer-compliance-notice" className="flex flex-col md:flex-row items-start gap-3 py-8 border-b border-white/10">
          <ShieldAlert size={18} className="text-rr-orangeLight shrink-0 mt-0.5" />
          <p className="text-xs text-white/50 leading-relaxed max-w-3xl">
            <span className="font-bold text-white/70">Compliance & Regulatory Notice:</span> RelayRigs is a technology marketplace and does not represent itself as a government agency, a motor carrier, a freight broker, an FMCSA/DOT-approved ELD, a licensed insurer, or a licensed escrow provider unless the applicable license, registration, or authorization actually exists for that specific service. Driver verification supports marketplace trust and does not constitute government certification.{" "}
            <Link to="/legal/compliance-information" data-testid="footer-compliance-link" className="font-bold text-rr-orangeLight hover:underline">
              Read full Compliance Information →
            </Link>
          </p>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 py-6 border-b border-white/10">
          {LEGAL.slice(5).map((l) => (
            <Link key={l.slug} to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="text-xs text-white/40 hover:text-white/70 transition-colors">
              {l.label}
            </Link>
          ))}
        </div>

        <div className="py-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/40">
          <p data-testid="footer-copyright">© 2026 RelayRigs. All rights reserved.</p>
          <p className="max-w-2xl">
            Regulatory, insurance, ELD, payment, and marketplace services depend on applicable requirements, jurisdictions, and third-party providers, and may vary or be unavailable in some regions.
          </p>
        </div>
      </div>
    </footer>
  );
};
